import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Button,
  Card,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Subscription } from 'src/constants/types';
import { FREE_CREDITS_HINT } from 'src/modules/companies/helpers';
import { CompanySubscriptionTabViewModel } from 'src/modules/companies/types';
import AdjustFreeCreditsDialog from '../AdjustFreeCreditsDialog';
import ChangePlanDialog from '../ChangePlanDialog';
import FreeCreditsHistory from '../FreeCreditsHistory';
import { LoadingSpinner } from '../shared/components';

const CARD_SX: SxProps<Theme> = { padding: 2.5 };

// A next period keeps the plan it was created with, so only the latest one changes plan
const hasNextPeriod = (subscription: Subscription, subscriptions: Subscription[]) =>
  subscriptions.some(
    other =>
      !!other.id &&
      other.id !== subscription.id &&
      (other.status === 'active' || other.status === 'pending') &&
      dayjs(other.endDate).isAfter(subscription.endDate),
  );

const changePlanHint = (hasUnsavedChanges: boolean, nextPeriod: boolean) => {
  if (hasUnsavedChanges) return 'Save or discard the changes first';
  if (nextPeriod) return 'Change the plan of the latest period';
  return 'Change plan';
};

const CompanySubscriptionTabView = (props: CompanySubscriptionTabViewModel) => {
  const {
    loading,
    company,
    isAdjustFreeCreditsOpen,
    onOpenAdjustFreeCredits,
    onCloseAdjustFreeCredits,
    onFreeCreditsAdjusted,
    freeCreditsHistoryRefreshKey,
    changePlanSubscription,
    onOpenChangePlan,
    onCloseChangePlan,
    onPlanChanged,
    subscriptions,
    plans,
    isSaveDisabled,
    hasUnsavedChanges,
    ConfirmationDialog,
    onAddNewSubscription,
    onChangeSubscriptionPlan,
    onChangeSubscriptionStartType,
    onChangeSubscriptionStartDate,
    onChangeSubscriptionAutoRenew,
    onDeleteSubscription,
    onSave,
  } = props;

  return (
    <Box sx={{ marginTop: 3 }}>
      {!loading && (
        <Stack gap={2}>
          <Card variant="outlined" sx={CARD_SX}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight="fontWeightSemiBold">
                Subscriptions
              </Typography>
              {subscriptions.filter(s => s.id === '').length === 0 && (
                <Button size="small" variant="contained" onClick={onAddNewSubscription}>
                  Add New Subscription
                </Button>
              )}
            </Stack>
            <Stack gap={3} sx={{ marginTop: 2.5 }}>
            {subscriptions &&
              subscriptions.map((s, index) => (
                <Stack key={s.id} gap={1.5}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <FormControl sx={{ width: index === 0 && !s.id ? '34%' : '49%' }}>
                      <InputLabel>Subscription</InputLabel>
                      <Select
                        MenuProps={{ disableScrollLock: true }}
                        value={s.plan?.id}
                        label={'Subscription'}
                        placeholder="Subscription"
                        disabled={
                          !!s.id || (subscriptions.length !== 1 && index !== subscriptions.length - 1)
                        }
                        onChange={e => {
                          onChangeSubscriptionPlan(s.id, e.target.value);
                        }}
                      >
                        {plans &&
                          plans
                            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                            .map(c => (
                              <MenuItem value={c.id} key={c.id}>
                                {c.name} (${c.price})
                              </MenuItem>
                            ))}
                      </Select>
                    </FormControl>
                    {index === 0 && (!s.id || s.startType === 'onboarding_complete') && (
                      <FormControl sx={{ width: 230 }}>
                        <InputLabel>Start Type</InputLabel>
                        <Select
                          MenuProps={{ disableScrollLock: true }}
                          value={s.startType}
                          label="Start Type"
                          disabled={!!s.id}
                          onChange={e =>
                            onChangeSubscriptionStartType(
                              s.id,
                              e.target.value as 'date' | 'onboarding_complete',
                            )
                          }
                        >
                          <MenuItem value="date">Specific date</MenuItem>
                          <MenuItem value="onboarding_complete">Account Setup Complete</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {((!s.id && index === 0 && s.startType === 'date') ||
                      s.startType === 'date' ||
                      index > 0) && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Start Date"
                          slotProps={{ textField: { size: 'small' } }}
                          minDate={dayjs(
                            index === 0
                              ? s.id
                              : dayjs(subscriptions[index - 1].endDate).add(1, 'd'),
                          )}
                          format="MM/DD/YYYY"
                          disabled={!!s.id}
                          closeOnSelect={false}
                          onChange={value => onChangeSubscriptionStartDate(s.id, value!)}
                          value={s.startDate ? dayjs(s.startDate.substring(0, 10)) : null}
                        />
                      </LocalizationProvider>
                    )}
                    {s.id && (s.status === 'active' || s.status === 'pending') && (
                      // Changing the plan reloads the list, which would drop the unsaved edits
                      <Tooltip title={changePlanHint(hasUnsavedChanges, hasNextPeriod(s, subscriptions))}>
                        <span>
                          <IconButton
                            color="primary"
                            aria-label="Change plan"
                            disabled={hasUnsavedChanges || hasNextPeriod(s, subscriptions)}
                            onClick={() => onOpenChangePlan(s)}
                          >
                            <SwapHorizIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <IconButton
                      color="primary"
                      aria-label="Delete subscription"
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteSubscription(s);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={4}>
                    {s.id && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          End Date
                        </Typography>
                        <Typography variant="body2">
                          {s.startType === 'onboarding_complete'
                            ? 'Account Setup Complete' + ' + ' + s.plan?.duration + ' ' + s.plan?.period + (s.plan?.duration !== 1 ? 's' : '')
                            : s.endDate && dayjs(s.endDate.substring(0, 10)).format('MM/DD/YYYY')}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        Auto Renew
                      </Typography>
                      <ToggleButtonGroup
                        size="small"
                        color="primary"
                        value={s.autoRenew}
                        exclusive
                        onChange={(_, value: boolean | null) => {
                          // A click on the selected button sends null rather than the other value
                          if (value !== null) {
                            onChangeSubscriptionAutoRenew(s.id, value);
                          }
                        }}
                        sx={{ '& .MuiToggleButton-root': { paddingY: 0.25, paddingX: 1.25 } }}
                      >
                        <ToggleButton value={false} selected={!s.autoRenew}>
                          Off
                        </ToggleButton>
                        <ToggleButton
                          value={true}
                          selected={s.autoRenew}
                          // Only the latest subscription can renew; the earlier ones can only stop
                          disabled={!s.autoRenew && index !== subscriptions.length - 1}
                        >
                          On
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Stack>
                </Stack>
              ))}
            </Stack>
            <Divider sx={{ marginTop: 2.5, marginX: -2.5 }} />
            <Stack direction="row" justifyContent="flex-end" sx={{ paddingTop: 2 }}>
              <Button disabled={isSaveDisabled} variant="contained" onClick={onSave}>
                Save
              </Button>
            </Stack>
          </Card>

          <Card variant="outlined" sx={CARD_SX}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography variant="subtitle1" fontWeight="fontWeightSemiBold">
                  Free Credits
                </Typography>
                <Tooltip title={FREE_CREDITS_HINT}>
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>
              <Button size="small" variant="contained" onClick={onOpenAdjustFreeCredits}>
                Adjust
              </Button>
            </Stack>
            <Stack direction="row" alignItems="baseline" gap={1} sx={{ marginTop: 1 }}>
              <Typography variant="h5">{company.freeCreditsRemaining.toLocaleString()}</Typography>
              <Typography variant="body2" color="text.secondary">
                credits available
              </Typography>
            </Stack>
            <FreeCreditsHistory company={company} refreshKey={freeCreditsHistoryRefreshKey} />
          </Card>
        </Stack>
      )}
      {loading && <LoadingSpinner />}
      <ConfirmationDialog />
      <AdjustFreeCreditsDialog
        open={isAdjustFreeCreditsOpen}
        company={company}
        onClose={onCloseAdjustFreeCredits}
        onAdjusted={onFreeCreditsAdjusted}
      />
      <ChangePlanDialog
        subscription={changePlanSubscription}
        plans={plans ?? []}
        company={company}
        onClose={onCloseChangePlan}
        onChanged={onPlanChanged}
        onFreeCreditsAdjusted={onFreeCreditsAdjusted}
      />
    </Box>
  );
};

export default CompanySubscriptionTabView;
